import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAppSelector } from '../store/hooks';
import { useI18n } from '../context/I18nContext';

const LANDING_SECTION_COUNT = 3;
const AUTO_SCROLL_INTERVAL_MS = 6500;
const AUTO_SCROLL_PAUSE_MS = 9000;

export const LandingScreen: React.FC = () => {
  const { isAuthenticated } = useAppSelector(state => state.auth);
  const { t } = useI18n();
  const pageRef = useRef<HTMLDivElement | null>(null);
  const sectionRefs = useRef<Array<HTMLElement | null>>([]);
  const scrollAnimationRef = useRef<number | null>(null);
  const interactionPauseUntilRef = useRef(0);
  const [activeSection, setActiveSection] = useState(0);
  const ctaHref = isAuthenticated ? '/boards' : '/auth?mode=register';
  const ctaLabel = isAuthenticated ? t('landing.cta.openApp') : t('landing.cta.signUp');

  const painPoints = useMemo(
    () => [
      {
        number: '01',
        title: t('landing.problem.scattered.title'),
        body: t('landing.problem.scattered.body'),
      },
      {
        number: '02',
        title: t('landing.problem.deadlines.title'),
        body: t('landing.problem.deadlines.body'),
      },
      {
        number: '03',
        title: t('landing.problem.team.title'),
        body: t('landing.problem.team.body'),
      },
    ],
    [t]
  );

  const solutions = useMemo(
    () => [
      {
        title: t('landing.solution.kanban.title'),
        body: t('landing.solution.kanban.body'),
      },
      {
        title: t('landing.solution.calendar.title'),
        body: t('landing.solution.calendar.body'),
      },
      {
        title: t('landing.solution.tasks.title'),
        body: t('landing.solution.tasks.body'),
      },
      {
        title: t('landing.solution.focus.title'),
        body: t('landing.solution.focus.body'),
      },
    ],
    [t]
  );

  const workflows = useMemo(
    () => [
      t('landing.workflow.plan'),
      t('landing.workflow.schedule'),
      t('landing.workflow.execute'),
      t('landing.workflow.review'),
    ],
    [t]
  );

  useEffect(() => {
    document.body.classList.add('landing-no-scroll');
    document.documentElement.classList.add('landing-no-scroll');

    return () => {
      document.body.classList.remove('landing-no-scroll');
      document.documentElement.classList.remove('landing-no-scroll');
    };
  }, []);

  useEffect(() => () => {
    if (scrollAnimationRef.current !== null && typeof window !== 'undefined') {
      window.cancelAnimationFrame(scrollAnimationRef.current);
      scrollAnimationRef.current = null;
    }
  }, []);

  useEffect(() => {
    const container = pageRef.current;
    if (!container || typeof window === 'undefined') return undefined;

    const pauseAutoScroll = () => {
      interactionPauseUntilRef.current = Date.now() + AUTO_SCROLL_PAUSE_MS;
    };

    container.addEventListener('wheel', pauseAutoScroll, { passive: true });
    container.addEventListener('touchstart', pauseAutoScroll, { passive: true });
    container.addEventListener('pointerdown', pauseAutoScroll);
    window.addEventListener('keydown', pauseAutoScroll);

    return () => {
      container.removeEventListener('wheel', pauseAutoScroll);
      container.removeEventListener('touchstart', pauseAutoScroll);
      container.removeEventListener('pointerdown', pauseAutoScroll);
      window.removeEventListener('keydown', pauseAutoScroll);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntry = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (!visibleEntry) return;
        const index = Number((visibleEntry.target as HTMLElement).dataset.sectionIndex || 0);
        setActiveSection(index);
      },
      { root: null, threshold: [0.45, 0.65, 0.85] }
    );

    sectionRefs.current.forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const scrollToSection = useCallback((index: number, durationOverride?: number) => {
    const target = sectionRefs.current[index];
    const container = pageRef.current;
    if (!target || !container || typeof window === 'undefined') return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      container.scrollTop = target.offsetTop;
      return;
    }

    if (scrollAnimationRef.current !== null) {
      window.cancelAnimationFrame(scrollAnimationRef.current);
      scrollAnimationRef.current = null;
    }

    const start = container.scrollTop;
    const end = target.offsetTop;
    const distance = end - start;
    if (Math.abs(distance) < 2) return;

    const duration = durationOverride ?? Math.min(2100, Math.max(1150, Math.abs(distance) * 1.2));
    const easeInOutQuint = (value: number) => {
      if (value < 0.5) return 16 * value * value * value * value * value;
      const shifted = -2 * value + 2;
      return 1 - (shifted * shifted * shifted * shifted * shifted) / 2;
    };

    let animationStart = 0;
    const animate = (timestamp: number) => {
      if (animationStart === 0) animationStart = timestamp;
      const elapsed = timestamp - animationStart;
      const progress = Math.min(1, elapsed / duration);
      const easedProgress = easeInOutQuint(progress);
      container.scrollTop = start + distance * easedProgress;

      if (progress < 1) {
        scrollAnimationRef.current = window.requestAnimationFrame(animate);
      } else {
        scrollAnimationRef.current = null;
      }
    };

    scrollAnimationRef.current = window.requestAnimationFrame(animate);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    const timer = window.setTimeout(() => {
      if (document.hidden) return;
      if (Date.now() < interactionPauseUntilRef.current) return;
      const nextSection = (activeSection + 1) % LANDING_SECTION_COUNT;
      scrollToSection(nextSection, 1900);
    }, AUTO_SCROLL_INTERVAL_MS);

    return () => window.clearTimeout(timer);
  }, [activeSection, scrollToSection]);

  const setSectionRef = (index: number) => (node: HTMLElement | null) => {
    sectionRefs.current[index] = node;
  };

  return (
    <div className="landing-page landing-page-v2 landing-product-page" ref={pageRef}>
      <div className="landing-dots" aria-label={t('landing.nav.sections')}>
        {[0, 1, 2].map((index) => (
          <button
            key={index}
            type="button"
            aria-label={t('landing.nav.goTo', { number: index + 1 })}
            aria-current={activeSection === index ? 'true' : undefined}
            className={`landing-dot ${activeSection === index ? 'is-active' : ''}`}
            onClick={() => {
              interactionPauseUntilRef.current = Date.now() + AUTO_SCROLL_PAUSE_MS;
              scrollToSection(index, 1750);
            }}
          />
        ))}
      </div>

      <section
        ref={setSectionRef(0)}
        data-section-index={0}
        className={`landing-screen landing-screen--hero ${activeSection === 0 ? 'is-active' : ''}`}
      >
        <div className="landing-content">
          <div className="landing-inner">
            <header className="landing-top">
              <Link className="landing-brand-mark" to="/">
                <span className="landing-brand-orbit" aria-hidden="true">B</span>
                <span>Boardly</span>
              </Link>
              <nav className="landing-main-nav" aria-label={t('landing.nav.product')}>
                <button type="button" onClick={() => scrollToSection(1, 1200)}>{t('landing.nav.problems')}</button>
                <button type="button" onClick={() => scrollToSection(2, 1200)}>{t('landing.nav.workflow')}</button>
              </nav>
            </header>

            <div className="landing-hero landing-product-hero">
              <div className="landing-copy">
                <p className="landing-eyebrow">{t('landing.eyebrow')}</p>
                <h1>
                  {t('landing.hero.line1')}
                  <br />
                  {t('landing.hero.line2')}
                  <br />
                  {t('landing.hero.line3')}
                </h1>
                <p>{t('landing.hero.description')}</p>

                <div className="landing-actions">
                  <Link className="landing-cta" to={ctaHref}>
                    {ctaLabel}
                  </Link>
                  <span>{t('landing.cta.note')}</span>
                </div>
              </div>

              <div className="landing-system-card" aria-hidden="true">
                <div className="landing-system-top">
                  <span />
                  <span />
                  <span />
                  <strong>{t('landing.mock.title')}</strong>
                </div>
                <div className="landing-system-grid">
                  <div className="landing-system-panel landing-system-panel--wide">
                    <small>{t('landing.mock.kanban')}</small>
                    <div className="landing-system-lanes">
                      <i />
                      <i />
                      <i />
                    </div>
                  </div>
                  <div className="landing-system-panel">
                    <small>{t('landing.mock.calendar')}</small>
                    <div className="landing-system-calendar">
                      {Array.from({ length: 14 }).map((_, index) => <i key={index} className={index === 6 || index === 10 ? 'is-filled' : ''} />)}
                    </div>
                  </div>
                  <div className="landing-system-panel">
                    <small>{t('landing.mock.focus')}</small>
                    <strong>2h 30m</strong>
                    <em>{t('landing.mock.focusNote')}</em>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        ref={setSectionRef(1)}
        data-section-index={1}
        className={`landing-screen landing-screen--productivity ${activeSection === 1 ? 'is-active' : ''}`}
      >
        <div className="landing-content landing-content--wide">
          <div className="landing-section-heading">
            <p className="landing-kicker">{t('landing.problem.kicker')}</p>
            <h2>{t('landing.problem.heading')}</h2>
            <p>{t('landing.problem.subtitle')}</p>
          </div>

          <div className="landing-problem-grid">
            {painPoints.map((item) => (
              <article className="landing-problem-card" key={item.title}>
                <span>{item.number}</span>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section
        ref={setSectionRef(2)}
        data-section-index={2}
        className={`landing-screen landing-screen--details ${activeSection === 2 ? 'is-active' : ''}`}
      >
        <div className="landing-content landing-content--wide">
          <div className="landing-section-heading">
            <p className="landing-kicker">{t('landing.solution.kicker')}</p>
            <h2>{t('landing.solution.heading')}</h2>
            <p>{t('landing.solution.subtitle')}</p>
          </div>

          <div className="landing-solution-grid">
            {solutions.map((item) => (
              <article className="landing-solution-card" key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </article>
            ))}
          </div>

          <div className="landing-workflow">
            <div>
              <p className="landing-kicker">{t('landing.workflow.kicker')}</p>
              <h2>{t('landing.workflow.heading')}</h2>
            </div>
            <ol>
              {workflows.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ol>
          </div>

          <div className="landing-signup">
            <h2>{t('landing.signup.title')}</h2>
            <p>{t('landing.signup.subtitle')}</p>
            <Link className="landing-cta" to={ctaHref}>
              {ctaLabel}
            </Link>
          </div>

          <footer className="landing-footer">
            <div>{t('help.footer.copyright')}</div>
            <div className="landing-footer-links">
              <Link to="/privacy-policy">{t('help.footer.privacyPolicy')}</Link>
              <span>{t('help.footer.terms')}</span>
              <span>{t('help.footer.security')}</span>
            </div>
          </footer>
        </div>
      </section>
    </div>
  );
};
